import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { gbp } from "../../lib/format";

function startOfMonth(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

type Tab = "by-home" | "by-category" | "by-claimant";

export default function ReportsPage() {
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [tab, setTab] = useState<Tab>("by-home");

  const summary = useQuery({
    queryKey: ["report-summary", from, to],
    queryFn: () => api.admin.report<{ claim_count: number; line_count: number; total_amount: number; total_vat: number; paid_amount: number; pending_amount: number }>("summary", { from, to }),
  });

  const detail = useQuery({
    queryKey: ["report", tab, from, to],
    queryFn: () => api.admin.report<any[]>(tab, { from, to }),
  });

  return (
    <div className="space-y-6">
      <Link to="/finance" className="text-sm text-slate-500 hover:text-slate-700">← Back to finance</Link>

      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Reports</h1>
        <p className="text-slate-600 mt-1">Approved + paid spend, filtered by expense date.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-slate-700 mb-1">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>
        </div>
      </div>

      {/* Summary tiles */}
      {summary.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-500">Total spend</div>
            <div className="text-2xl font-semibold text-liaise-navy mt-1">{gbp(Number(summary.data.total_amount ?? 0))}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-500">VAT</div>
            <div className="text-2xl font-semibold text-liaise-navy mt-1">{gbp(Number(summary.data.total_vat ?? 0))}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-500">Paid</div>
            <div className="text-2xl font-semibold text-emerald-700 mt-1">{gbp(Number(summary.data.paid_amount ?? 0))}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs text-slate-500">Awaiting payment</div>
            <div className="text-2xl font-semibold text-amber-700 mt-1">{gbp(Number(summary.data.pending_amount ?? 0))}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        {([
          { id: "by-home", label: "By home" },
          { id: "by-category", label: "By category" },
          { id: "by-claimant", label: "By claimant" },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id ? "border-liaise-navy text-liaise-navy" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {detail.isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {detail.error && <div className="px-6 py-12 text-red-600 text-sm">{(detail.error as Error).message}</div>}
        {detail.data && detail.data.length === 0 && (
          <div className="px-6 py-12 text-center text-slate-500">No spend in this date range.</div>
        )}
        {detail.data && detail.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              {tab === "by-home" && (
                <tr>
                  <th className="px-6 py-3 font-medium">Code</th>
                  <th className="px-6 py-3 font-medium">Home</th>
                  <th className="px-6 py-3 font-medium">Sub-brand</th>
                  <th className="px-6 py-3 font-medium text-right">Claims</th>
                  <th className="px-6 py-3 font-medium text-right">Lines</th>
                  <th className="px-6 py-3 font-medium text-right">Total</th>
                </tr>
              )}
              {tab === "by-category" && (
                <tr>
                  <th className="px-6 py-3 font-medium">Code</th>
                  <th className="px-6 py-3 font-medium">Category</th>
                  <th className="px-6 py-3 font-medium">Funding</th>
                  <th className="px-6 py-3 font-medium text-right">Lines</th>
                  <th className="px-6 py-3 font-medium text-right">Total</th>
                </tr>
              )}
              {tab === "by-claimant" && (
                <tr>
                  <th className="px-6 py-3 font-medium">Claimant</th>
                  <th className="px-6 py-3 font-medium text-right">Claims</th>
                  <th className="px-6 py-3 font-medium text-right">Lines</th>
                  <th className="px-6 py-3 font-medium text-right">Total</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {detail.data.map((row, i) => (
                <tr key={i}>
                  {tab === "by-home" && (
                    <>
                      <td className="px-6 py-3 font-mono text-xs">{row.code}</td>
                      <td className="px-6 py-3">{row.cost_centre_name}</td>
                      <td className="px-6 py-3 text-slate-600">{row.sub_brand ?? "—"}</td>
                      <td className="px-6 py-3 text-right">{row.claim_count}</td>
                      <td className="px-6 py-3 text-right">{row.line_count}</td>
                      <td className="px-6 py-3 text-right font-medium">{gbp(Number(row.total_amount))}</td>
                    </>
                  )}
                  {tab === "by-category" && (
                    <>
                      <td className="px-6 py-3 font-mono text-xs">{row.code}</td>
                      <td className="px-6 py-3">{row.category_name}</td>
                      <td className="px-6 py-3 text-slate-600">{row.funding_source}</td>
                      <td className="px-6 py-3 text-right">{row.line_count}</td>
                      <td className="px-6 py-3 text-right font-medium">{gbp(Number(row.total_amount))}</td>
                    </>
                  )}
                  {tab === "by-claimant" && (
                    <>
                      <td className="px-6 py-3">
                        {row.claimant_entra_oid ? (
                          <Link to={`/admin/claimant/${row.claimant_entra_oid}`} className="font-medium text-slate-700 hover:text-liaise-navy hover:underline">
                            {row.claimant_display_name}
                          </Link>
                        ) : (
                          <div className="font-medium text-slate-700">{row.claimant_display_name}</div>
                        )}
                        <div className="text-xs text-slate-500">{row.claimant_email}</div>
                      </td>
                      <td className="px-6 py-3 text-right">{row.claim_count}</td>
                      <td className="px-6 py-3 text-right">{row.line_count}</td>
                      <td className="px-6 py-3 text-right font-medium">{gbp(Number(row.total_amount))}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
